// Terrain provider: real elevation + water topology for an arbitrary bounding box.
//
// Primary source:  AWS Terrain Tiles (Mapzen "terrarium" encoding, global ~30m DEM,
//                  public S3 bucket, no API key).
// Fallback:        Open-Meteo elevation API (90m Copernicus DEM, batched, no key).
// Water bodies:    OSM polygons via Overpass (natural=water etc.) + at/below-sea-level
//                  detection for ocean.

import {
  latLonToWorldPx,
  cellCenterLatLon,
  pointInRing,
  ringBbox,
} from "./geo";
import { overpassQuery } from "./overpass";

const TERRARIUM_URL = (z, x, y) =>
  `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`;

const TILE_SIZE = 256;

// Pick a zoom whose pixel resolution is finer than the grid cell size
const pickZoom = (cellSizeM, midLat) => {
  for (let z = 14; z >= 6; z--) {
    const metersPerPx =
      (156543.03392 * Math.cos((midLat * Math.PI) / 180)) / Math.pow(2, z);
    if (metersPerPx >= cellSizeM / 4) return z;
  }
  return 6;
};

const decodeTerrarium = (r, g, b) => r * 256 + g + b / 256 - 32768;

// Fetch one terrarium tile and return its ImageData (or null on failure)
const fetchTileImageData = async (z, x, y) => {
  const resp = await fetch(TERRARIUM_URL(z, x, y), { mode: "cors" });
  if (!resp.ok) throw new Error(`Tile ${z}/${x}/${y}: HTTP ${resp.status}`);
  const blob = await resp.blob();
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

// --- Primary provider: AWS Terrain Tiles ---------------------------------
const fetchElevationFromTiles = async (bbox, dims, onProgress) => {
  const midLat = (bbox.south + bbox.north) / 2;
  const cellSizeM =
    ((bbox.east - bbox.west) * 111320 * Math.cos((midLat * Math.PI) / 180)) /
    dims.cols;
  const z = pickZoom(cellSizeM, midLat);

  // Tile range covering the bbox
  const nw = latLonToWorldPx(bbox.north, bbox.west, z);
  const se = latLonToWorldPx(bbox.south, bbox.east, z);
  const tx0 = Math.floor(nw.px / TILE_SIZE);
  const ty0 = Math.floor(nw.py / TILE_SIZE);
  const tx1 = Math.floor(se.px / TILE_SIZE);
  const ty1 = Math.floor(se.py / TILE_SIZE);

  const tiles = new Map(); // "x,y" -> ImageData
  const jobs = [];
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      jobs.push({ tx, ty });
    }
  }
  if (jobs.length > 64) {
    throw new Error(
      `Region too large for elevation tiles (${jobs.length} tiles). Reduce the radius.`
    );
  }

  let done = 0;
  await Promise.all(
    jobs.map(async ({ tx, ty }) => {
      const img = await fetchTileImageData(z, tx, ty);
      tiles.set(`${tx},${ty}`, img);
      done++;
      onProgress?.(`Elevation tiles ${done}/${jobs.length}`);
    })
  );

  // Sample elevation (bilinear) at every grid cell center
  const sample = (lat, lon) => {
    const { px, py } = latLonToWorldPx(lat, lon, z);
    const x0 = Math.floor(px - 0.5);
    const y0 = Math.floor(py - 0.5);
    const fx = px - 0.5 - x0;
    const fy = py - 0.5 - y0;
    const read = (wx, wy) => {
      const tx = Math.floor(wx / TILE_SIZE);
      const ty = Math.floor(wy / TILE_SIZE);
      const img = tiles.get(`${tx},${ty}`);
      if (!img) return null;
      const ix = Math.min(TILE_SIZE - 1, Math.max(0, wx - tx * TILE_SIZE));
      const iy = Math.min(TILE_SIZE - 1, Math.max(0, wy - ty * TILE_SIZE));
      const idx = (iy * TILE_SIZE + ix) * 4;
      return decodeTerrarium(
        img.data[idx],
        img.data[idx + 1],
        img.data[idx + 2]
      );
    };
    const e00 = read(x0, y0);
    const e10 = read(x0 + 1, y0);
    const e01 = read(x0, y0 + 1);
    const e11 = read(x0 + 1, y0 + 1);
    if (e00 === null) return e10 ?? e01 ?? e11 ?? 0;
    const top = e00 * (1 - fx) + (e10 ?? e00) * fx;
    const bot = (e01 ?? e00) * (1 - fx) + (e11 ?? e00) * fx;
    return top * (1 - fy) + bot * fy;
  };

  const elevation = Array(dims.rows)
    .fill(null)
    .map(() => new Float32Array(dims.cols));
  for (let r = 0; r < dims.rows; r++) {
    for (let c = 0; c < dims.cols; c++) {
      const { lat, lon } = cellCenterLatLon(bbox, dims, r, c);
      elevation[r][c] = sample(lat, lon);
    }
  }
  return { elevation, source: `AWS Terrain Tiles (z${z})` };
};

// --- Fallback provider: Open-Meteo elevation API -------------------------
const fetchElevationFromOpenMeteo = async (bbox, dims, onProgress) => {
  const elevation = Array(dims.rows)
    .fill(null)
    .map(() => new Float32Array(dims.cols));
  const points = [];
  for (let r = 0; r < dims.rows; r++) {
    for (let c = 0; c < dims.cols; c++) {
      const { lat, lon } = cellCenterLatLon(bbox, dims, r, c);
      points.push({ r, c, lat, lon });
    }
  }
  const BATCH = 100;
  for (let i = 0; i < points.length; i += BATCH) {
    const batch = points.slice(i, i + BATCH);
    const lats = batch.map((p) => p.lat.toFixed(5)).join(",");
    const lons = batch.map((p) => p.lon.toFixed(5)).join(",");
    const resp = await fetch(
      `https://api.open-meteo.com/v1/elevation?latitude=${lats}&longitude=${lons}`
    );
    if (!resp.ok) throw new Error(`Open-Meteo elevation: HTTP ${resp.status}`);
    const json = await resp.json();
    json.elevation.forEach((e, j) => {
      elevation[batch[j].r][batch[j].c] = e;
    });
    onProgress?.(
      `Elevation ${Math.min(i + BATCH, points.length)}/${points.length} cells`
    );
  }
  return { elevation, source: "Open-Meteo elevation API" };
};

// --- Water mask ----------------------------------------------------------
// Returns rows x cols Uint8Array grid; 1 = water.
export const fetchWaterMask = async (
  bbox,
  dims,
  elevation,
  onProgress,
  { endpointOffset = 0 } = {}
) => {
  const water = Array(dims.rows)
    .fill(null)
    .map(() => new Uint8Array(dims.cols));

  // Ocean / large water: at or below sea level
  for (let r = 0; r < dims.rows; r++) {
    for (let c = 0; c < dims.cols; c++) {
      if (elevation[r][c] <= 0.5) water[r][c] = 1;
    }
  }

  // Inland water bodies from OSM
  try {
    onProgress?.("Fetching water bodies (OSM)…");
    const bboxStr = `${bbox.south},${bbox.west},${bbox.north},${bbox.east}`;
    const query = `
      [out:json][timeout:45];
      (
        way["natural"="water"](${bboxStr});
        way["waterway"="riverbank"](${bboxStr});
        relation["natural"="water"](${bboxStr});
      );
      out geom;`;
    const json = await overpassQuery(query, { endpointOffset });
    const rings = [];
    for (const el of json.elements || []) {
      if (el.type === "way" && el.geometry?.length >= 3) {
        rings.push(el.geometry.map((g) => ({ lat: g.lat, lon: g.lon })));
      } else if (el.type === "relation" && el.members) {
        for (const m of el.members) {
          if (m.role === "outer" && m.geometry?.length >= 3) {
            rings.push(m.geometry.map((g) => ({ lat: g.lat, lon: g.lon })));
          }
        }
      }
    }
    for (const ring of rings) {
      const rb = ringBbox(ring);
      for (let r = 0; r < dims.rows; r++) {
        for (let c = 0; c < dims.cols; c++) {
          if (water[r][c]) continue;
          const { lat, lon } = cellCenterLatLon(bbox, dims, r, c);
          if (
            lat < rb.south ||
            lat > rb.north ||
            lon < rb.west ||
            lon > rb.east
          )
            continue;
          if (pointInRing(lat, lon, ring)) water[r][c] = 1;
        }
      }
    }
  } catch (err) {
    console.warn("OSM water lookup failed; using sea-level water only.", err);
  }
  return water;
};

// --- Public API ----------------------------------------------------------
// Returns { elevation (m, Float32Array rows), source }. Water is fetched
// separately via fetchWaterMask so the caller can sequence Overpass traffic.
export const fetchElevation = async (bbox, dims, onProgress) => {
  try {
    onProgress?.("Fetching elevation tiles…");
    return await fetchElevationFromTiles(bbox, dims, onProgress);
  } catch (err) {
    console.warn("Terrain tiles failed, falling back to Open-Meteo:", err);
    onProgress?.("Falling back to Open-Meteo elevation…");
    return await fetchElevationFromOpenMeteo(bbox, dims, onProgress);
  }
};
